/**
 * 第四階段（家庭）：媽媽的早安訊息、陽台菜園和戚風蛋糕。
 * 標示「英文練習」的句子（玩家選的回覆等）在所有語言中都刻意保留英文。
 */
import type { Messages } from './en';

export const zhTWFamily: Pick<Messages, 'family'> = {
  family: {
    mapNames: { balcony: '陽台' },
    zones: { balcony: '陽台菜園' },
    objects: {
      fam_plot: '花盆',
      fam_tap: '水龍頭',
    },
    npc: {},
    items: {
      tomato: { name: '番茄', desc: '自己陽台種的，被太陽曬得暖暖的，又多汁。吃了體力 +20。' },
      sunflower: { name: '向日葵', desc: '你親手種出來、笑咪咪的大朵向日葵。看著它，心情直接補滿到 100。' },
      chiffon: { name: '家庭號戚風蛋糕', desc: '用三顆自己種的草莓做的戚風蛋糕，又高又蓬鬆。可以帶回家跟家人分享，也可以犒賞自己。' },
    },

    // --- 媽媽的早安訊息 -----------------------------------------------------------
    menuMom: '📩 媽媽的訊息',
    toastNew: '📩 媽媽傳訊息給你了！',
    previewNone: '還沒有訊息',
    you: '你',
    today: '今天',
    sad: '😢',
    loveUp: (n: number) => `媽媽 ❤️ +${n}`,
    loveDown: '媽媽看起來有點難過……',
    ps: {
      water: 'P.S. 陽台的菜記得要澆水喔 🌱',
      ripe: 'P.S. 陽台上好像有東西可以採了！🍅🍓',
    },
    /**
     * 媽媽的訊息。`msgs` 是媽媽傳的（"@card" = 早安圖）；`replies` 是玩家的選項
     * （英文練習，保留英文），每個選項附媽媽的回覆，和可選的下一輪。
     */
    texts: {
      rain: {
        icon: '☔',
        msgs: ['寶貝早安 ☔', '今天會下雨喔，要帶傘！藍色那把在鞋櫃旁邊。'],
        replies: [
          {
            me: 'Got it, Mom! I’ll take the umbrella ☂️', tone: 'warm', love: 5,
            mom: ['乖孩子 👍', '還有，不准穿制服鞋去踩水坑。我太了解你了 😏'],
            next: [
              { me: 'Just ONE little puddle? 🥺', tone: 'warm', love: 3, mom: ['哈哈哈。好啦，一個就好，很小很小的那種。', '回家記得把鞋子放門口晾乾喔！'] },
              { me: 'I promise. No puddles!', tone: 'ok', mom: ['等我看到乾乾的襪子再說 🧦😆'] },
            ],
          },
          {
            me: 'It’s only a little rain. I’ll be fine.', tone: 'ok',
            mom: ['台灣的小雨，五分鐘就變大雨啦！🌧️', '拜託帶著嘛，就當作為了媽媽？'],
            next: [
              { me: 'Okay, okay, I’ll take it. For you ❤️', tone: 'warm', love: 4, mom: ['謝謝寶貝，這樣媽媽上班就不用擔心了 😊'] },
              { me: 'Mom, you worry too much. Stop it.', tone: 'rude', mom: ['……好吧。', '當媽的本來就會擔心。淋濕了要打給我。'] },
            ],
          },
          {
            me: 'Ugh, umbrellas are so annoying.', tone: 'rude',
            mom: ['感冒才更麻煩好嗎……', '你妹妹都乖乖帶雨衣了，連她都聽我的話！'],
            next: [
              { me: 'Sorry, Mom. I’ll take it.', tone: 'warm', love: 3, mom: ['謝謝 🥰 這才是我的孩子。'] },
              { me: 'Fine… but it’s ugly.', tone: 'ok', mom: ['那就用你的零用錢買一把可愛的啊！😂'] },
            ],
          },
        ],
      },
      storm: {
        icon: '⛈️',
        msgs: ['⛈️ 下午有雷陣雨！', '傘放在書包裡了。如果打雷閃電就待在室內等，不要站在樹下！'],
        replies: [
          {
            me: 'I’ll wait inside until it stops. Promise! ⛈️', tone: 'warm', love: 6,
            mom: ['不愧是我聰明的孩子 🧠✨', '如果雨真的很大，打給我，我去接你。'],
            next: [
              { me: 'Thanks, Mom. Can we have hot soup tonight? 🍲', tone: 'warm', love: 3, mom: ['煮你最愛的玉米濃湯，一言為定 😋'] },
              { me: 'Can Dad come instead? His car has music 🎶', tone: 'ok', mom: ['哼。我的機車也有音樂啊，我會唱！🛵🎤😂'] },
            ],
          },
          {
            me: 'Thunder is cool! Can I watch the lightning?', tone: 'ok',
            mom: ['在「窗戶」看可以，在操場看「不行」⚡', '寶貝，閃電不是在看影片啦 😅'],
            next: [
              { me: 'Haha, okay. From the window. Love you!', tone: 'warm', love: 4, mom: ['媽媽更愛你 ❤️ 不要淋濕喔！'] },
              { me: 'You never let me do anything fun.', tone: 'rude', mom: ['我讓你做很多好玩的事啊……只是不讓你做危險的。', '今天晚上我們一起邊吃零食邊看雷雨，好不好？'] },
            ],
          },
          {
            me: 'I’m not a baby. Stop texting me.', tone: 'rude',
            mom: ['好。媽媽只是愛你而已。'],
            next: [{ me: 'Sorry, Mom… I love you too.', tone: 'warm', love: 4, mom: ['🥹 好啦。快去，不然要遲到了！'] }],
          },
        ],
      },
      sunny: {
        icon: '☀️',
        msgs: ['早安！☀️ 今天天氣晴朗又溫暖。', '要多喝水！水壺裝滿了——不要又整壺原封不動帶回來 😤'],
        replies: [
          {
            me: 'I’ll drink it ALL. Every drop! 💧', tone: 'warm', love: 5,
            mom: ['哈哈哈，我晚上會檢查水壺喔 🕵️‍♀️'],
            next: [
              { me: 'Deal! And I’ll fill it again at school.', tone: 'warm', love: 3, mom: ['哇，你是誰？我的孩子去哪了？😂❤️'] },
              { me: 'What do I get if it’s empty?', tone: 'ok', mom: ['一個抱抱。說不定星期天再加一杯珍奶 🧋', '半糖喔！'] },
            ],
          },
          {
            me: 'But the school water tastes funny.', tone: 'ok',
            mom: ['就是水啊！好啦……放一片檸檬進去。', '冰箱裡有檸檬 🍋'],
            next: [
              { me: 'Lemon water! Good idea, thanks Mom 🍋', tone: 'warm', love: 4, mom: ['媽媽點子最多了，去跟你爸說 😎'] },
              { me: 'Lemon water is gross.', tone: 'rude', mom: ['阿嬤喝檸檬水喝了 80 年耶……', '好啦，那就喝白開水。總之給我「喝」！'] },
            ],
          },
          {
            me: 'Water is boring. I want soda.', tone: 'rude',
            mom: ['汽水的糖「超」多的……', '你的牙齒會哭，然後媽媽也會哭。'],
            next: [{ me: 'Okay, water. For my teeth and for you 😁', tone: 'warm', love: 3, mom: ['很好！晚上給我看你亮晶晶的牙齒 😁🦷'] }],
          },
        ],
      },
      hot: {
        icon: '🥵',
        msgs: ['🥵 今天會到 33 度！', '戴帽子，每節下課都要喝水，中午待在陰涼的地方。'],
        replies: [
          {
            me: 'Hat on! I look super cool 🧢', tone: 'warm', love: 5,
            mom: ['全台灣最帥的孩子 😎', '防曬乳也要擦！在門口旁邊的架子上。'],
            next: [
              { me: 'Got it! Sunscreen, hat, water ✅', tone: 'warm', love: 3, mom: ['檢查清單完美！媽媽老師給你 100 分 💯'] },
              { me: 'Sunscreen is sticky…', tone: 'ok', mom: ['黏黏的總比曬得跟蝦子一樣紅好 🦐😂'] },
            ],
          },
          {
            me: 'Can I buy a shaved ice after school? 🍧', tone: 'ok',
            mom: ['嗯……一碗小的芒果冰。', '前提是水壺要先喝光喔！'],
            next: [
              { me: 'Yes! Thank you, best Mom ever! 🥭', tone: 'warm', love: 4, mom: ['全世界最棒的媽媽也想吃一口 😋'] },
              { me: 'Can I get a BIG one?', tone: 'ok', mom: ['小碗就好，你的肚子很小 😆'] },
            ],
          },
          {
            me: 'I hate hats. I’m not wearing it.', tone: 'rude',
            mom: ['那太陽會曬到你頭痛喔……', '至少待在陰涼的地方，好不好？'],
            next: [{ me: 'Sorry. I’ll wear it, Mom.', tone: 'warm', love: 3, mom: ['謝謝寶貝。到了八月你就會感謝我了 😂'] }],
          },
        ],
      },
      cool: {
        icon: '🍂',
        msgs: ['早安 🍂 今天變涼了，只有 18 度。', '外套要穿上！不是拿著——是「穿」上 🧥'],
        replies: [
          {
            me: 'Jacket is ON. Zipped up! 🧥', tone: 'warm', love: 5,
            mom: ['好可愛又好溫暖 😍', '保溫瓶裡還有熱飲喔。'],
            next: [
              { me: 'Thanks, Mom! Is it ginger tea? 🫚', tone: 'warm', love: 3, mom: ['黑糖薑茶，阿嬤的獨門配方 ❤️'] },
              { me: 'Do I have to drink all of it?', tone: 'ok', mom: ['對，要喝完。它有魔法，可以趕走感冒 🪄😆'] },
            ],
          },
          {
            me: '18 degrees isn’t cold!', tone: 'ok',
            mom: ['在台灣這叫「冷死了」🥶😂', '相信我，學校每個人都會穿羽絨外套。'],
            next: [
              { me: 'Haha, okay. I’ll wear it so you don’t worry.', tone: 'warm', love: 4, mom: ['謝謝寶貝。媽媽開心，就不會碎碎念了 😌'] },
              { me: 'Down jackets are for old people.', tone: 'rude', mom: ['我就穿羽絨外套啊……', '好啦好啦，至少帶著，拜託？'] },
            ],
          },
          {
            me: 'I don’t need a jacket. Leave me alone.', tone: 'rude',
            mom: ['等你感冒了，誰煮湯給你喝？我。'],
            next: [{ me: 'Sorry, Mom… I’ll wear it.', tone: 'warm', love: 3, mom: ['很好。媽媽還是會煮湯，以防萬一 🍲'] }],
          },
        ],
      },
      typhoon: {
        icon: '🌀',
        msgs: ['🌀 颱風假！停班停課，大家都待在家。', '可以幫媽媽做防颱準備嗎？把陽台的盆栽搬進來、瓶子裝滿水、手電筒充電 🔦'],
        replies: [
          {
            me: 'Of course! I’ll help with everything 💪', tone: 'warm', love: 7,
            mom: ['我的小英雄 🦸', '先搬盆栽，趁風還沒變大！'],
            next: [
              { me: 'On it! Plants first, then the water bottles.', tone: 'warm', love: 3, mom: ['好團隊！弄完我們來吃泡麵、玩撲克牌 🍜🃏'] },
              { me: 'Can we watch the typhoon from the window?', tone: 'ok', mom: ['離玻璃遠一點就可以。窗戶可能會破！', '先在窗戶貼膠帶 🙂'] },
            ],
          },
          {
            me: 'A typhoon day! Can I play games all day? 🎮', tone: 'ok',
            mom: ['哈哈哈，全台灣的小孩都這樣說 😂', '先幫忙，再打電動。一言為定？'],
            next: [
              { me: 'Deal! Helping first 💪', tone: 'warm', love: 4, mom: ['這才是我的孩子！媽媽煮你最愛的麵 🍜'] },
              { me: 'No. It’s a holiday!', tone: 'rude', mom: ['對搶修電線的人來說可不是放假喔……', '我們要懂得感恩，幫一點忙，好嗎？'] },
            ],
          },
          {
            me: 'That’s boring. Ask my sister.', tone: 'rude',
            mom: ['你妹妹才六歲，搬不動花盆啦。', '媽媽今天真的很需要你。'],
            next: [{ me: 'Sorry. I’ll help you, Mom.', tone: 'warm', love: 4, mom: ['謝謝寶貝。我們一起平平安安 ❤️'] }],
          },
        ],
      },
      exam: {
        icon: '📝',
        msgs: ['今天考試加油！📝', '我在你便當裡放了一顆粽子。「包中」——保證考上！😆'],
        replies: [
          {
            me: 'Thanks, Mom! I studied a lot. I’ll do my best! 📚', tone: 'warm', love: 6,
            mom: ['我知道，昨晚看到你房間燈還亮著 💡', '不管考幾分，媽媽都以你為榮。'],
            next: [
              { me: 'That means a lot. Love you ❤️', tone: 'warm', love: 3, mom: ['❤️❤️❤️ 好啦，去讓考卷知道誰才是老大！'] },
              { me: 'Even if I get a 60?', tone: 'ok', mom: ['60 分也是。不過 61 分更好 😂', '每一題都要看兩遍！'] },
            ],
          },
          {
            me: 'I’m so nervous…', tone: 'ok',
            mom: ['慢慢深呼吸三次。吸……吐…… 🌬️', '會緊張代表你在乎，這是好事。'],
            next: [
              { me: 'Okay… breathing. Thanks, Mom. I feel better.', tone: 'warm', love: 5, mom: ['你一定可以的！媽媽在公司幫你加油 📣'] },
              { me: 'What if I forget everything?', tone: 'ok', mom: ['不會的。就算真的忘了，也只是一次考試，不是你的整個人生 🤗'] },
            ],
          },
          {
            me: 'Don’t remind me! You’re making it worse!', tone: 'rude',
            mom: ['對不起……我只是想幫你加油。'],
            next: [{ me: 'Sorry, Mom. I’m just stressed. Thank you.', tone: 'warm', love: 4, mom: ['我知道，寶貝。大大的抱抱 🤗 粽子能量！'] }],
          },
        ],
      },
      dinner: {
        icon: '🍗',
        msgs: ['早安！猜猜看今天晚餐吃什麼？😋', '是三杯雞！九層塔放超多，香噴噴 🌿'],
        replies: [
          {
            me: 'YES! My favorite! Can I help you cook? 🍗', tone: 'warm', love: 6,
            mom: ['當然好！你負責摘九層塔和攪鍋子。', '六點前到家喔，小主廚！👩‍🍳'],
            next: [
              { me: 'Chef reporting for duty at six! 🫡', tone: 'warm', love: 3, mom: ['哈哈哈，主廚！我去幫你找圍裙 😆'] },
              { me: 'Can I just do the tasting part? 😋', tone: 'ok', mom: ['負責試吃的人要洗碗喔 😏'] },
            ],
          },
          {
            me: 'What are the three cups?', tone: 'ok',
            mom: ['麻油、米酒、醬油——各一杯！🍶', '現在你知道我們家的祕密了 🤫'],
            next: [
              { me: 'Cool! Will you teach me the recipe someday?', tone: 'warm', love: 4, mom: ['改天？這個週末就教你！🥰'] },
              { me: 'Is there a cup of chocolate? 🍫', tone: 'ok', mom: ['哈哈哈，才沒有！那是甜點啦，傻孩子 😂'] },
            ],
          },
          {
            me: 'Chicken again? Boring.', tone: 'rude',
            mom: ['媽媽一大早去市場買雞耶……'],
            next: [
              { me: 'Sorry, Mom. I love your three-cup chicken. Really!', tone: 'warm', love: 4, mom: ['哼……好吧。最大塊的給你 😌'] },
              { me: 'Can we have pizza instead?', tone: 'ok', mom: ['披薩星期六吃，今天吃三杯雞。這是媽媽的規定 📏'] },
            ],
          },
        ],
      },
      card: {
        icon: '🌻',
        card: { theme: 1, big: '早安', sub: '平安健康 天天快樂' },
        msgs: ['@card', '親愛的早安 🌸🌸 天天平安健康！🙏'],
        replies: [
          {
            me: 'Good morning, Mom! Pretty flowers 🌻', tone: 'warm', love: 5,
            mom: ['很漂亮吧？林阿姨先傳給我的 😆', '我已經轉傳給四十個人了。'],
            next: [
              { me: 'Forty?! You’re the queen of good-morning pictures 👑', tone: 'warm', love: 3, mom: ['謝謝謝謝 👸 我手機裡還存了 300 張 😂'] },
              { me: 'Mom… you don’t have to send one every day 😅', tone: 'ok', mom: ['不傳的話，你怎麼知道我平安？😂', '一天一張圖 = 媽媽很好！'] },
            ],
          },
          {
            me: '@card', tone: 'warm', love: 5,
            mom: ['哇！我的孩子傳圖給「我」耶！🥹', '我要拿去給公司的人看。'],
            next: [
              { me: 'Please don’t show everyone 😳', tone: 'ok', mom: ['來不及了。陳太太說你好可愛 😆'] },
              { me: 'Tell them I said hi! 👋', tone: 'warm', love: 2, mom: ['大家都跟你說嗨！你出名了 🌟'] },
            ],
          },
          {
            me: 'Not another one of those pictures 🙄', tone: 'rude',
            mom: ['那張我挑了三分鐘耶……'],
            next: [{ me: 'Sorry, Mom. It IS pretty 🌸', tone: 'warm', love: 3, mom: ['嘿嘿。明天我挑一張有貓咪的 🐱'] }],
          },
        ],
      },
    },
    /** 「回傳一張早安圖」選項的標籤。 */
    sendCard: '回傳一張早安圖 🖼️',
    replyCard: { theme: 4, big: '早安', sub: '媽媽我愛妳' },

    // --- 陽台菜園 ----------------------------------------------------------------
    crops: {
      strawberry: { name: '草莓', icon: '🍓', seed: '草莓種子' },
      tomato: { name: '番茄', icon: '🍅', seed: '番茄種子' },
      sunflower: { name: '向日葵', icon: '🌻', seed: '向日葵種子' },
    },
    stages: ['空的土', '幼苗', '新芽', '成長中', '成熟了！'],
    plotTitle: (n: number) => `花盆 ${n}`,
    emptyText: 'Soft, dark soil. What do you want to plant?', // 英文練習
    plant: (seed: string) => `🌱 種下${seed}`,
    free: '免費',
    notNow: '先不要',
    noCoins: '錢不夠買這些種子。',
    planted: (crop: string) => `You planted ${crop}! Water it once a day.`, // 英文練習
    watered: (n: number) => `💧 澆好水了！（${n}/3）`,
    rainWatered: '🌧️ 今天的雨幫你的植物澆過水了。',
    alreadyWatered: '土還是濕的，明天再來吧！',
    canEmpty: '澆水壺空了——去水龍頭裝水。',
    harvest: (what: string) => `Harvest time! You picked ${what}.`, // 英文練習
    growText: (crop: string, stage: string, n: number) => `${crop} · ${stage} · 已澆水 ${n}/3 天`,
    covered: '植物蓋著防水布，很安全，等颱風過去就好。',
    tapTitle: '水龍頭',
    tapText: '一個生鏽的老水龍頭，下面放著藍色水桶，澆水壺就在旁邊。',
    fillCan: (n: number) => `🪣 把澆水壺裝滿（可澆 ${n} 次）`,
    canFilled: '咕嚕咕嚕……澆水壺裝滿了！',
    fillBottle: '💧 把水壺裝滿',
    bottleFilled: '你的水壺又裝滿了。',
    canState: (n: number) => `澆水壺：還剩 ${n} 次`,
    plantsSafe: '🪴 陽台的盆栽都安全搬進屋裡了，想得真周到！',
    typhoonWarn: '🌀 今天颱風要來！天黑前把陽台的盆栽搬進來。',
    typhoonHit: '🌀 昨晚的颱風把陽台的植物都吹倒了……要重新種了。',
    tomatoEat: '又甜又多汁！體力 +20。',
    sunflowerLook: '向日葵對著你笑，你覺得心情超好！',

    // --- 烤箱：戚風蛋糕 ------------------------------------------------------------
    chiffon: '🎂 烤一個家庭號戚風蛋糕',
    chiffonNeeds: '戚風蛋糕需要 3 顆草莓。去陽台種吧！',
    chiffonWork: '分蛋、打蛋白霜、拌入草莓……',
    chiffonDone: '像雲一樣又高又蓬鬆！家庭號戚風蛋糕完成！',
    chiffonShare: '大家都圍過來了。「好軟喔！像雲一樣！」全家人都笑開了。',
    chiffonEat: '你吃了一大塊鬆軟的蛋糕，好幸福！',

    journal: {
      harvest: '從陽台菜園採收了{arg}',
      chiffon: '用自己種的草莓烤了一個家庭號戚風蛋糕',
    },
  },
};
